import { useMemo, useState } from "react"
import {
  useShareSupport,
  useShareVehicleImages,
  useVehicleImageIds,
} from "hooks/useShareVehicleImage"
import { Button } from "reactstrap"
import { toast } from "react-toastify"

const ShareVehicleImages = ({ vehicleId }) => {
  const { data, isLoading } = useVehicleImageIds(vehicleId)
  const { mutate: shareImages } = useShareVehicleImages()
  const [sharingChunks, setSharingChunks] = useState({})

  const vehicleIds = useMemo(
    () => (data?.image_ids ?? []).map(id => ({ id, imgType: "vehicle" })),
    [data?.image_ids]
  )

  const auctionIds = useMemo(
    () =>
      data?.auction_id ? [{ id: data.auction_id, imgType: "auction" }] : [],
    [data?.auction_id]
  )

  const ids = useMemo(
    () => [...auctionIds, ...vehicleIds],
    [vehicleIds, auctionIds]
  )
  const totalImages = ids.length

  // Split ids into chunks of 10 images per share
  const maxFilesPerChunk = 10

  const chunks = useMemo(() => {
    const result = []
    for (let i = 0; i < ids.length; i += maxFilesPerChunk) {
      result.push(ids.slice(i, i + maxFilesPerChunk))
    }
    return result
  }, [ids])

  const handleShareChunk = chunkIndex => {
    if (!chunks[chunkIndex]?.length) return

    setSharingChunks(prev => ({ ...prev, [chunkIndex]: true }))

    shareImages(
      {
        vehicleId,
        imageIds: chunks[chunkIndex],
        onProgress: (shared, total) => {
          console.log(
            `Sharing ${chunkIndex + 1}: ${shared} / ${total} images completed`
          )
        },
      },
      {
        onSuccess: data => {
          setSharingChunks(prev => ({ ...prev, [chunkIndex]: false }))
          toast.success(
            `Successfully shared images!`
          )
        },
        onError: error => {
          setSharingChunks(prev => ({ ...prev, [chunkIndex]: false }))

          if (error.message.includes("BROWSER_NOT_SUPPORTED")) {
            toast.error("Please use Chrome, Safari, or Edge to share files")
          } else if (error.message.includes("FILE_SHARING_NOT_SUPPORTED")) {
            toast.error(
              "Your browser doesn't support file sharing. Try updating your browser."
            )
          } else if (error.message.includes("permission denied")) {
            toast.error(
              `Sharing permission denied. Check browser settings and allow file sharing.`
            )
          } else {
            toast.error("Sharing failed: " + error.message)
          }
        },
      }
    )
  }

  const shareSupport = useShareSupport()

  if (!shareSupport.isSupported) {
    return <div>Sharing is not supported in your browser</div>
  }

  return (
    <>
      <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
        <div>
          You can share a maximum of {shareSupport.limits.maxFiles} images at a
          time.
        </div>
        <small>
          Browser Limits Per Share: {shareSupport.limits.maxFiles} images,{" "}
          {shareSupport.limits.maxSizeFormatted} total
        </small>
      </div>

      <div className="d-flex gap-2 align-items-center flex-wrap">
        {chunks.map((chunk, index) => {
          const start = index * maxFilesPerChunk + 1
          const end = Math.min((index + 1) * maxFilesPerChunk, totalImages)

          return (
            <Button
              key={index}
              onClick={() => handleShareChunk(index)}
              disabled={isLoading || sharingChunks[index]}
              className="mb-2 btn-success d-flex align-items-center gap-2"
            >
              <i className="fas fa-share-alt"></i>
              {sharingChunks[index] ? (
                <small>
                  Sharing images {start}–{end} of {totalImages}...
                </small>
              ) : (
                <small>
                  Share images {start}–{end} of {totalImages}
                </small>
              )}
            </Button>
          )
        })}
      </div>
    </>
  )
}

export default ShareVehicleImages
